// NoteViewer.js
//
// Build 26: reusable note display/edit modal used by both NotesApp (when
// users open their own notes) and Desktop (when users click a note
// shortcut). One component, multiple modes:
//
//   mode='view'         read-only display
//   mode='edit'         editable with ReactQuill
//
// Both modes support a fullscreen toggle and a Print button. The viewer
// also exposes Copy Link and Pin to Desktop actions where the note's
// permalink is relevant.
//
// Why one component instead of two? The view and edit modes share most
// of the markup — title, metadata, content area, action toolbar. Forking
// would duplicate the toolbar and the fullscreen logic. Keep it together.

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { printHtml } from './utils/printUtils';
import {
  buildPermalink, copyToClipboard, pinEntityToDesktop
} from './utils/shortcutHelpers';
import './NoteViewer.css';

const QUILL_TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link', 'blockquote', 'code-block'],
  [{ color: [] }, { background: [] }],
  ['clean']
];

function NoteViewer({
  note,                     // The note object to display
  mode = 'view',            // 'view' | 'edit'
  onClose,                  // () => void
  onSave,                   // (updates) => Promise — only used in edit mode
  onDelete,                 // () => void — optional
  currentUserId,            // For "Pin to desktop" action
  showPinToDesktop = true,  // Whether to show the Pin button (off for shared/read-only)
  showCopyLink = true,      // Whether to show the Copy Link button
  embedded = false,         // Build 28: hosted inside a FloatingWindow
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [editTitle, setEditTitle] = useState(note?.title || '');
  const [editContent, setEditContent] = useState(note?.content || '');
  const [editCategory, setEditCategory] = useState(note?.category || '');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);  // {type, text}

  // Reset local edit state when the note prop changes (e.g. user switches
  // which note they're viewing without closing the modal).
  useEffect(() => {
    setEditTitle(note?.title || '');
    setEditContent(note?.content || '');
    setEditCategory(note?.category || '');
  }, [note?.id, note?.title, note?.content, note?.category]);

  // Escape key closes the modal. Esc is the standard expectation; pulling
  // this into the component means every consumer gets it for free.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (isFullscreen) setIsFullscreen(false);
        else onClose?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen, onClose]);

  if (!note) return null;

  // Auto-dismiss the feedback toast after a couple seconds.
  const flash = (type, text) => {
    setFeedback({ type, text });
    setTimeout(() => setFeedback(null), 2200);
  };

  const handleSave = async () => {
    if (!onSave) return;
    if (!editTitle.trim()) {
      flash('error', 'Title cannot be empty');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        title: editTitle.trim(),
        content: editContent,
        category: editCategory || null
      });
      flash('success', 'Saved');
    } catch (err) {
      console.error('Save failed:', err);
      flash('error', `Save failed: ${err?.message || 'unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLink = async () => {
    const url = buildPermalink('/notes', note.id);
    const ok = await copyToClipboard(url);
    flash(ok ? 'success' : 'error', ok ? 'Link copied to clipboard' : 'Could not copy link');
  };

  const handlePin = async () => {
    if (!currentUserId) {
      flash('error', 'Sign in to pin to desktop');
      return;
    }
    try {
      await pinEntityToDesktop({
        userId: currentUserId,
        type: 'note',
        entityId: note.id,
        title: note.title,
        icon: '📝'
      });
      flash('success', 'Pinned to desktop');
    } catch (err) {
      console.error('Pin failed:', err);
      flash('error', `Could not pin: ${err?.message || 'unknown error'}`);
    }
  };

  const handlePrint = () => {
    const metaParts = [];
    if (note.category) metaParts.push(`Category: ${escapeText(note.category)}`);
    if (note.createdAt?.toDate) {
      metaParts.push(`Created: ${note.createdAt.toDate().toLocaleString()}`);
    }
    if (note.updatedAt?.toDate) {
      metaParts.push(`Last updated: ${note.updatedAt.toDate().toLocaleString()}`);
    }

    // For the body: in edit mode the user may have unsaved changes — print
    // what's currently in the editor. In view mode print the stored content.
    const bodyHtml = mode === 'edit' ? editContent : (note.content || '');
    const titleForPrint = mode === 'edit' ? editTitle : note.title;

    printHtml({
      title: titleForPrint || 'Untitled note',
      bodyHtml: bodyHtml || '<em>(no content)</em>',
      metaHtml: metaParts.join(' &nbsp;·&nbsp; ')
    });
  };

  // Render
  // Build 28: embedded mode (hosted in a FloatingWindow) drops the
  // fixed full-screen backdrop and fills the window body instead. The
  // window's titlebar owns closing, so no click-out-to-close. Inner
  // JSX is identical either way.
  //
  // v1.0.1 fix: when embedded AND the user hits Fullscreen, BREAK OUT
  // of the window — render the normal fixed full-screen overlay so the
  // note covers the whole screen (pre-Build-28 behavior). Escape (the
  // keydown handler above) flips isFullscreen off, collapsing it back
  // into its window. Previously the embedded branch ignored
  // isFullscreen entirely, so the button appeared to do nothing.
  const useEmbeddedChrome = embedded && !isFullscreen;
  const overlayClass = useEmbeddedChrome
    ? 'note-viewer-overlay note-viewer-overlay--embedded'
    : `note-viewer-overlay ${isFullscreen ? 'note-viewer-overlay--fullscreen' : ''}`;
  const modalClass = useEmbeddedChrome
    ? 'note-viewer-modal note-viewer-modal--embedded'
    : `note-viewer-modal ${isFullscreen ? 'note-viewer-modal--fullscreen' : ''}`;

  const tree = (
    <div
      className={overlayClass}
      onClick={(e) => {
        if (useEmbeddedChrome) return;
        // Only close if clicking the backdrop, not the modal body.
        // (In broken-out fullscreen we still don't want click-out to
        // close — that would be surprising mid-edit — so guard on
        // embedded too.)
        if (embedded) return;
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className={modalClass} onClick={(e) => e.stopPropagation()}>
        <div className="note-viewer-header">
          <div className="note-viewer-title-row">
            <span className="note-viewer-icon">📝</span>
            {mode === 'edit' ? (
              <input
                type="text"
                className="note-viewer-title-input"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Note title"
                aria-label="Note title"
              />
            ) : (
              <h2 className="note-viewer-title">{note.title || 'Untitled'}</h2>
            )}
          </div>
          <div className="note-viewer-toolbar no-print">
            {showCopyLink && (
              <button
                className="note-viewer-tool-btn"
                onClick={handleCopyLink}
                title="Copy a shareable link to this note"
              >
                🔗 Copy Link
              </button>
            )}
            {showPinToDesktop && (
              <button
                className="note-viewer-tool-btn"
                onClick={handlePin}
                title="Pin this note to your desktop"
              >
                📎 Pin to Desktop
              </button>
            )}
            <button
              className="note-viewer-tool-btn"
              onClick={handlePrint}
              title="Print this note"
            >
              🖨️ Print
            </button>
            <button
              className="note-viewer-tool-btn"
              onClick={() => setIsFullscreen(v => !v)}
              title={isFullscreen ? 'Exit fullscreen' : 'Expand to fullscreen'}
            >
              {isFullscreen ? '🗗 Exit fullscreen' : '🗖 Fullscreen'}
            </button>
            <button
              className="note-viewer-close-btn"
              onClick={onClose}
              title="Close (Esc)"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        {/* Optional metadata strip */}
        <div className="note-viewer-meta">
          {note.category && (
            <span className="note-viewer-meta-item">
              Category: <strong>{note.category}</strong>
            </span>
          )}
          {note.createdAt?.toDate && (
            <span className="note-viewer-meta-item">
              Created {note.createdAt.toDate().toLocaleDateString()}
            </span>
          )}
          {note.updatedAt?.toDate && note.updatedAt !== note.createdAt && (
            <span className="note-viewer-meta-item">
              Updated {note.updatedAt.toDate().toLocaleDateString()}
            </span>
          )}
        </div>

        <div className="note-viewer-body">
          {mode === 'edit' ? (
            <ReactQuill
              theme="snow"
              value={editContent}
              onChange={setEditContent}
              modules={{ toolbar: QUILL_TOOLBAR }}
              className="note-viewer-editor"
            />
          ) : (
            <div
              className="note-viewer-content-readonly"
              dangerouslySetInnerHTML={{ __html: note.content || '<em>(empty)</em>' }}
            />
          )}
        </div>

        {/* Footer actions — only in edit mode */}
        {mode === 'edit' && (
          <div className="note-viewer-footer no-print">
            {onDelete && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={onDelete}
                disabled={saving}
              >
                🗑️ Delete
              </button>
            )}
            <div className="note-viewer-footer-right">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}

        {/* Feedback toast (transient, lives inside the modal) */}
        {feedback && (
          <div className={`note-viewer-toast note-viewer-toast--${feedback.type}`}>
            {feedback.text}
          </div>
        )}
      </div>
    </div>
  );

  // Build 28 v1.0.2: when a windowed note breaks out to fullscreen,
  // render via a portal at document.body so it escapes the
  // FloatingWindow's `overflow: hidden` clip and stacking context
  // entirely. Without the portal the overlay was trapped inside the
  // scrollable window body (top cut off, exit button unreachable). The
  // non-fullscreen embedded case stays inline so it fills the window.
  if (embedded && isFullscreen) {
    return createPortal(tree, document.body);
  }
  return tree;
}

function escapeText(s) {
  return String(s).replace(/[<>&"']/g, c => ({
    '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;'
  }[c]));
}

export default NoteViewer;
